"use client"

import posthog from 'posthog-js'
import { SPEC_FAQ_MIN_RENDER, type SpecFaq } from '@/lib/faq/spec-faq-builder'
import { moduleFlag } from '@/lib/pip/module-flags'

// CTA row paused (owner hotfix 2026-07-11): unset = dev-preview only,
// NEXT_PUBLIC_MODULE_FAQ_CTA=1 + redeploy relaunches. The row is non-schema
// UI (§5.1), so gating it never touches the FAQPage JSON-LD.
const SHOW_CTA_ROW = moduleFlag('FAQ_CTA')

interface SpecFAQProps {
  faqs: SpecFaq[]
  /** live purchasable offers — drives the (non-schema) CTA row */
  offerCount: number
  /** anchor id of the offers/buy-box area to scroll to. Defaults to the TOP
   * "Current Offers" buy-box card (`current-offers`), where the price is
   * visible on landing (owner item 5, 07/20) — NOT the mid-list "show more"
   * toggle, which is a data-testid, not a real anchor target. */
  offersAnchorId?: string
}

/**
 * Data-grounded spec FAQ — FAQ rewrite §5.1 (docs/Page-Types-And-Templates.md).
 *
 * Renders the buildSpecFaqs() output verbatim. The FAQPage JSON-LD for these
 * items is emitted SERVER-SIDE in page.tsx (single-schema rule) — this
 * component must never add its own <script> block.
 *
 * Real-or-Hidden: self-hides below 2 items. The trailing "Compare offers" row
 * is a UI element, not a Q&A — it is intentionally absent from the schema feed.
 */
export function SpecFAQ({ faqs, offerCount, offersAnchorId = 'current-offers' }: SpecFAQProps) {
  if (!faqs || faqs.length < SPEC_FAQ_MIN_RENDER) return null

  return (
    <section
      id="faq"
      className="mt-12 pt-10 border-t border-gray-200"
      aria-label="Frequently asked questions"
      data-testid="spec-faq"
      data-faq-count={faqs.length}
    >
      <header className="mb-6">
        <p className="text-[#ff9951] font-bold tracking-widest text-xs uppercase mb-2">
          Before You Buy
        </p>
        <h2 className="text-2xl md:text-3xl font-black tracking-tighter text-gray-900 leading-tight">
          Frequently Asked Questions
        </h2>
      </header>

      <div className="space-y-4">
        {faqs.map((f) => (
          <details
            key={f.slot}
            className="group rounded-lg overflow-hidden transition-colors bg-[#0469f0]/[0.02] open:bg-[#0469f0]/[0.05]"
            onToggle={(e) => {
              if ((e.currentTarget as HTMLDetailsElement).open) {
                posthog?.capture?.('pdp_faq_open', { source: 'spec', slot: f.slot })
              }
            }}
          >
            <summary className="w-full p-5 flex items-center justify-between gap-4 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40">
              <span className="font-bold text-gray-900 text-sm pr-4">{f.question}</span>
              <svg
                aria-hidden="true"
                className="w-5 h-5 text-gray-500 flex-shrink-0 transition-transform group-open:rotate-180"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">
              {f.answer}
              {f.toolLink && (
                <>
                  {' '}
                  {f.toolLink.prefix}
                  <a
                    href={f.toolLink.href}
                    className="text-[#0469f0] font-semibold underline decoration-[#0469f0]/30 underline-offset-2 hover:decoration-[#0469f0]"
                  >
                    {f.toolLink.text}
                  </a>
                  .
                </>
              )}
            </div>
          </details>
        ))}
      </div>

      {SHOW_CTA_ROW && offerCount > 0 && (
        <a
          href={`#${offersAnchorId}`}
          onClick={() => posthog?.capture?.('pdp_faq_cta_click', { offer_count: offerCount })}
          className="mt-6 flex items-center justify-between rounded-lg bg-[#0469f0] px-5 py-4 text-white font-bold text-sm hover:bg-[#0357c9] transition-colors"
          data-testid="spec-faq-cta"
        >
          <span>
            Compare {offerCount} {offerCount === 1 ? 'offer' : 'offers'} for this model
          </span>
          <span aria-hidden="true">↑</span>
        </a>
      )}
    </section>
  )
}

export default SpecFAQ
